
import React, { useState, useCallback, useEffect } from 'react';
import { Card, CardContent, Grid, Typography, TextField, Autocomplete} from '@mui/material';
import { RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, Tooltip, ResponsiveContainer } from 'recharts';
import axiosConfig from '../config';
import { formatDate } from '../util';
import _ from 'lodash';
import { FieldNameMappings, ValueMappings } from '../enum';

/** PLAN:
 *  
 *  Preliminary part: Search up a patient,a nd select from dropdown
 * 
 *  Has basic patient information on the left (chid, name, sex, age) 
 *  Has operation information on the right (surgeon full name, surgery type, surgery side, provisional theatre date)
 * 
 * 
 *  Then, underneath....
 *  FIRST Part: Bluelock, Priority Score Edition [Left]
 *  Uses: !!!! "SimpleRadarChart" !!!!!
 * ...I can't be asked to make this professional, so just have my previous commit: This will represent the priority score as if they're Bluelock player. 
 *  It'll look cool. Trust. 
 *  Can be upgraded to SpecifiedDomainChart. Will upgrade it if I have time. :( 
 * 
 * 
 *  SECOND PART: Average Comparisons  [Right]
 *  Can compare a *specific* patient to the average demographic category.
 *  ...Maybe this one could be the SpecifiedDomainChart? Not
 *  
 */

export default function IndividualPatientAnalytics() {
  const [loading, setLoading] = useState(true);
  const [averages, setAverages] = useState({});
  const [patientSearch, setPatientSearch] = useState("");
  const [patientList, setPatientList] = useState([]);
  const [operations, setOperations] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);

  const fetchPatients = useCallback(async () => {

    const res = await axiosConfig.get(`/patients`, {
      params: {
        search: patientSearch,
        page: 1,
        pageSize: 5,
      }
    });

    setPatientList(res.data.patients);

  }, [patientSearch, setPatientList]);

    const fetchOperations = useCallback(async () => {

    if (_.isEmpty(selectedPatient))
      return;
    const res = await axiosConfig.get(`/patients/${selectedPatient.chId}/operations`);

    setOperations(res.data.operations);

  }, [setOperations, selectedPatient]);

  const fetchAverages = useCallback(async () => {

    if (!selectedPatient)
      return;

    const res = await axiosConfig.get(`/operations/analytics/metrics`, {
      params: {
        fields: "painLevel,mobility,adl,radioSeverity,waitingMonths"
      }
    });

    setAverages(res.data.metrics);

  }, [setAverages, selectedPatient]);

  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      await fetchAverages();
      await fetchPatients();
      await fetchOperations();
      setLoading(false);
    }
    fetchAll();

  }, [fetchAverages, fetchPatients, fetchOperations, setLoading]);

  // We need to merge the data of the radar (the patient's stuff) and the average (average of X bracket) into one file, so that 
  // it can be represented properly in the file. I believe in Piotr, now, to fix it. 
  const radarFields = ["painLevel", "adl", "mobility", "radioSeverity", "waitingMonths"];

  const mergedData = _.map(radarFields, (field) => ({
    subject: field !== "waitingMonths" ? FieldNameMappings[field] : "Waiting Years"  ,
    ..._.fromPairs(operations.map((val, i) => [i, field !== "waitingMonths" ? val[field] : val[field]/12])),
    average: field !== "waitingMonths" ? averages[field] : (averages[field])/12
  }))

  // console.log(mergedData, operations?.[0]?.waitingMonths/12 ?? 0)

  return (
    <div style={{ padding: '2rem' }}>
      <Typography variant="h4" gutterBottom>
        Individual Patient Analytics
      </Typography>


      {/** For the nice little search bar on top. Autocompletes from the list of patients. */}
        <Autocomplete
          options={patientList}
          getOptionLabel={(option) => `${option.chId} - ${option.name.forename} ${option.name.surname}`}
          renderInput={(params) => (
            <TextField {...params} label= "Search Patient by CHI Number" variant="outlined" onChange={(evt) => setPatientSearch(evt.target.value || "")} />
          )}
          style={{ marginBottom: '2rem', width: 400 }}
          value={selectedPatient}
          onChange={(e, newValue) => setSelectedPatient(newValue)}
        />

      <Grid container spacing={2}>
        {/* Patient Information Card */}
        <Grid item xs={12} md={6}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" color="primary"> Patient Information </Typography>
              <Typography><strong> Name:</strong> {selectedPatient ? selectedPatient?.name.forename + " " + selectedPatient?.name.surname : "\u2013"} </Typography>
              <Typography><strong> CHI: </strong> {selectedPatient?.chId ?? "\u2013"} </Typography>
              <Typography><strong> Age: </strong> {selectedPatient?.age ?? "\u2013"} </Typography>
              <Typography><strong> Sex: </strong> {selectedPatient?.sex ?? "\u2013"} </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Surgery Information Card */}
        <Grid item xs={12} md={6}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" color="primary"> Surgery Information </Typography>
              <Typography><strong> Surgeon: </strong> {operations[0]?.surgeonId ?? "\u2013"}</Typography>
              <Typography><strong> Surgery Type: </strong> {ValueMappings.operationType?.[operations[0]?.operationType] ?? "\u2013"}</Typography>
              <Typography><strong> Surgery Side: </strong> {ValueMappings.surgerySide?.[operations[0]?.surgerySide] ?? "\u2013"}</Typography>
              <Typography><strong> Provisional Date: </strong> {operations[0] ? formatDate(operations[0]?.theatreDate) : "\u2013"}</Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* guys nagi is gone :(  */}
        { loading ? <div>Loading... please wait!</div> :
          <>
          <Grid item xs={12} md={6}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" color="primary"> Priority Score Visualiser </Typography>
                <Typography gutterBottom><strong>Total Score</strong>: {operations[0]?.priorityScore ?? "\u2013"} </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <RadarChart data={mergedData}>
                    <PolarGrid />
                    <PolarAngleAxis dataKey="subject" />
                    <PolarRadiusAxis angle={30} domain={[0, 4]} />
                    <Radar name="Operation 1" dataKey={0} stroke="#1976d2" fill="#1976d2" fillOpacity={0.6} />
                    { _.has(mergedData, 1) &&
                      <Radar name="Operation 2" dataKey={1} stroke="#1976d2" fill="#1976d2" fillOpacity={0.6} />
                    }
                    <Tooltip />
                  </RadarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>

          {/* Comparison Chart. Here's where the merged data comes into play. */}
          <Grid item xs={12} md={6}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" color="primary"> Average Comparisons </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <RadarChart data={mergedData}>
                    <PolarGrid />
                    <PolarAngleAxis dataKey="subject" />
                    <PolarRadiusAxis angle={30} domain={[0, 4]} />
                    <Radar name="Operation 1" dataKey="0" stroke="#1976d2" fill="#1976d2" fillOpacity={0.6} />
                    { _.has(mergedData, 1) &&
                      <Radar name="Operation 2" dataKey={1} stroke="#1976d2" fill="#1976d2" fillOpacity={0.6} />
                    }
                    <Radar name="Average" dataKey="average" stroke="#f44336" fill="#f44336" fillOpacity={0.3} />
                    <Tooltip />
                  </RadarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>
          </>
        }
      </Grid>
    </div>
  );
}
